import { useEffect } from "react";

/* ─── Site-wide SEO constants ─────────────────────────────────────────────── */
export const SITE = {
  name: "WonderfulInterview",
  legalName: "WonderfulInterview, a Wonderful Agent product",
  url: "https://interview.wonderfulagent.ai",
  twitter: "@wonderfulagent",
  // 1200×630 social card (valid JPEG, renders on every scraper)
  ogImage:
    "https://images.unsplash.com/photo-1454496522488-7a8e488e8606?auto=format&fit=crop&w=1200&h=630&q=80",
  email: "hello@wonderfulagent.ai",
};

export type SeoProps = {
  /** Full <title> text (include the brand). */
  title: string;
  description: string;
  /** Route path beginning with "/" — used for canonical + og:url. */
  path: string;
  /** Absolute image URL; falls back to the site OG image. */
  image?: string;
  type?: "website" | "article";
  noindex?: boolean;
  /** JSON-LD structured data object (or array of objects). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Client-rendered SEO head manager for the marketing SPA. Sets title, meta
 * description, canonical, Open Graph, Twitter card and JSON-LD per route.
 * Static fallbacks live in index.html for non-JS crawlers and first paint.
 */
export function Seo({ title, description, path, image, type = "website", noindex = false, jsonLd }: SeoProps) {
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    const url = SITE.url + path;
    const img = image ?? SITE.ogImage;

    document.title = title;
    setMeta("name", "description", description);
    setMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    setLink("canonical", url);

    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", SITE.name);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);
    setMeta("name", "twitter:site", SITE.twitter);

    const id = "route-jsonld";
    let script = document.getElementById(id) as HTMLScriptElement | null;
    if (jsonLdStr) {
      if (!script) {
        script = document.createElement("script");
        script.id = id;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = jsonLdStr;
    } else if (script) {
      script.remove();
    }
  }, [title, description, path, image, type, noindex, jsonLdStr]);

  return null;
}

/* ─── Reusable JSON-LD builders ───────────────────────────────────────────── */
export const ORG_JSONLD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE.name,
  url: SITE.url,
  email: SITE.email,
  logo: `${SITE.url}/favicon.svg`,
  description:
    "Pay-as-you-go AI interview platform. Create AI video interviews, let candidates self-schedule by email, and receive an AI-written evaluation with built-in integrity checks.",
  sameAs: ["https://wonderfulagent.ai"],
};

export function breadcrumb(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: SITE.url + it.path,
    })),
  };
}

export function faqJsonLd(faqs: { q: string; a: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
